import type { KernelHandle } from '#types/types';

export interface BulkSelectableOutput {
  id: string;
  pending?: boolean;
  draft?: boolean;
  failed?: boolean;
}

export interface WorkspaceBulkSelectableOutput extends BulkSelectableOutput {
  workspaceId: string;
}

export const resolveBulkDeleteTargets = <T extends BulkSelectableOutput>(
  selectedIds: ReadonlySet<string>,
  outputs: readonly T[],
): T[] => {
  const normalizedSelectedIds = new Set(
    Array.from(selectedIds, (id) => id.trim()).filter(Boolean),
  );
  const seen = new Set<string>();
  return outputs.filter((output) => {
    const outputId = output.id.trim();
    if (!outputId || !normalizedSelectedIds.has(outputId) || output.pending || output.draft || output.failed || seen.has(outputId)) return false;
    seen.add(outputId);
    return true;
  });
};

export const groupBulkDeleteTargetsByWorkspace = <T extends WorkspaceBulkSelectableOutput>(
  outputs: readonly T[],
): Map<string, string[]> => {
  const grouped = new Map<string, string[]>();
  outputs.forEach((output) => {
    const workspaceId = output.workspaceId.trim();
    const outputId = output.id.trim();
    if (!workspaceId || !outputId) return;
    const ids = grouped.get(workspaceId) ?? [];
    if (!ids.includes(outputId)) ids.push(outputId);
    grouped.set(workspaceId, ids);
  });
  return grouped;
};

export const deleteBulkTargetsByWorkspace = <T extends WorkspaceBulkSelectableOutput>(
  outputs: readonly T[],
  deleteVersions: (
    workspaceId: string,
    versionIds: string[],
  ) => { deletedIds: string[]; skippedIds: string[] },
): Set<string> => {
  const failedIds = new Set<string>();
  for (const [workspaceId, versionIds] of groupBulkDeleteTargetsByWorkspace(outputs)) {
    try {
      const result = deleteVersions(workspaceId, versionIds);
      const deletedIds = new Set(result.deletedIds);
      versionIds.forEach((id) => {
        if (!deletedIds.has(id)) failedIds.add(id);
      });
    } catch {
      versionIds.forEach((id) => failedIds.add(id));
    }
  }
  return failedIds;
};

type DeletableListOutput = WorkspaceBulkSelectableOutput & { isProjectLayer?: boolean };
type ListOutputDeleter = Pick<KernelHandle, 'deleteWorkspaceVersions' | 'mutateInventory' | 'setLayer'>;

/** Deletes List outputs the way the grid's Delete does; returns the ids that could not be deleted. */
export const deleteListOutputs = async (kernel: ListOutputDeleter, targets: readonly DeletableListOutput[]): Promise<Set<string>> => {
  const failedIds = deleteBulkTargetsByWorkspace(
    targets.filter((output) => !output.isProjectLayer),
    (workspaceId, versionIds) => kernel.deleteWorkspaceVersions(workspaceId, versionIds),
  );
  for (const target of targets.filter((output) => output.isProjectLayer)) {
    try {
      // Deleting the current ProjectLayer must not destroy the historical fallback. The
      // projection will intentionally expose that legacy WorkspaceVersion again when no
      // detached representation remains.
      await kernel.mutateInventory([{ action: 'DELETE', entity: 'StudioLayer', id: target.id }] as Parameters<KernelHandle['mutateInventory']>[0], `Delete ProjectLayer output ${target.id}`);
      kernel.setLayer(target.id, null);
    } catch {
      failedIds.add(target.id);
    }
  }
  return failedIds;
};
